// src/components/ResultContentWrapper.jsx - MATCHES CSS FILES
import React from 'react';
import { useQuizContext } from '../hooks/useQuizContext';
import ResultContentCard from './ResultContentCard';
import ProgramList from './ProgramList';
import EmailCaptureForm from './EmailCaptureForm';
import '../styles/mwc-layout.css';

/**
 * ResultContentWrapper
 * Displays quiz results with ONLY user-relevant information
 * NO internal scoring data, mindset counts, area counts, etc.
 */
const ResultContentWrapper = () => {
  const { state } = useQuizContext();
  const {
    resultData = null,
    programs = [],
    resultContents = []
  } = state;

  // session is the computed Session doc
  const session = resultData || null;

  // Prefer items fetched into context; fall back to anything included on the session
  const rcFromSession = resultData?.resultContents || resultData?.resultContent || [];
  const resultContent = (resultContents && resultContents.length) ? resultContents : rcFromSession;

  // Get personalized title (user-facing)
  const personalizedTitle = session?.personalizedTitle || 'Your College Journey';

  // Get user info (user-facing)
  const firstName = session?.firstName || '';
  const sessionId = session?.sessionId || '';
  
  // TODO: Get tenantId from config or context
  const tenantId = 'demo-university';

  return (
    <div className="mwc-results-wrapper">
      {/* Hero Section with Personalized Title */}
      <section className="mwc-results-hero">
        <div className="mwc-results-hero__badge">
          ✨ Your Personalized Results
        </div>
        <h1 className="mwc-results-hero__title">
          {firstName ? `${firstName}, you're a ` : ''}
          {personalizedTitle}
        </h1>
        <p className="mwc-results-hero__subtitle">
          We've matched you with programs and resources based on your interests and goals.
        </p>
        
        {/* Simple Stats - ONLY USER-MEANINGFUL DATA */}
        <div className="mwc-results-hero__stats">
          <div className="mwc-results-hero__stat">
            <div className="mwc-results-hero__stat-value">
              {programs?.length ?? 0}
            </div>
            <div className="mwc-results-hero__stat-label">
              Programs Matched
            </div>
          </div>
          <div className="mwc-results-hero__stat">
            <div className="mwc-results-hero__stat-value">
              {resultContent?.length ?? 0}
            </div>
            <div className="mwc-results-hero__stat-label">
              Helpful Resources
            </div>
          </div>
        </div>
      </section>

      {/* Email Capture Section - AT THE TOP of results */}
      {sessionId && (
        <section className="mwc-results-section">
          <EmailCaptureForm 
            sessionId={sessionId}
            tenantId={tenantId}
            firstName={firstName}
          />
        </section>
      )}

      {/* Recommended Programs Section */}
      <section className="mwc-card mwc-results-section mwc-results-programs">
        <h2 className="mwc-results-section__title">
          🎓 Programs Perfect for You
        </h2>
        <p className="mwc-results-section__description">
          These programs align with your interests and educational goals.
        </p>
        {programs && programs.length ? (
          <ProgramList programs={programs} />
        ) : (
          <div className="mwc-results-empty">
            <div className="mwc-results-empty__icon">🎓</div>
            <p className="mwc-results-empty__text">
              No program recommendations available at this time.
            </p>
            <p className="mwc-results-empty__subtext">
              Contact us to learn more about programs that might be right for you.
            </p>
          </div>
        )}
      </section>

      {/* Recommended Resources Section */}
      <section className="mwc-card mwc-results-section mwc-results-content">
        <h2 className="mwc-results-section__title">
          📚 Resources to Guide Your Journey
        </h2>
        <p className="mwc-results-section__description">
          Explore these resources to help you make informed decisions about your education.
        </p>
        {resultContent && resultContent.length ? (
          <div className="mwc-results-content__grid">
            {resultContent.map(rc => (
              <ResultContentCard key={rc._id || rc.tag || rc.title} content={rc} />
            ))}
          </div>
        ) : (
          <div className="mwc-results-empty">
            <div className="mwc-results-empty__icon">📭</div>
            <p className="mwc-results-empty__text">
              No additional resources found.
            </p>
            <p className="mwc-results-empty__subtext">
              Check back soon for more helpful content!
            </p>
          </div>
        )}
      </section>
    </div>
  );
};

export default ResultContentWrapper;